import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { BehaviorSubject, forkJoin } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';

import { AuthService } from '../shared/services/auth.service';
import { BackendService } from '../shared/services/backend.service';
import { TimeOffInfoComponent } from '../shared/components/time-off-info/time-off-info.component';
import { AppliedTimeOff } from '../shared/models/time-off.model';
import { sortPendingApprovalsByToDateAsc } from './sort-pending-by-to-date.util';

@Injectable({
    providedIn: 'root'
})
export class TimeOffApprovalsService {
    pendingApprovalsLoading$ = new BehaviorSubject<boolean>(true);
    pendingApprovalsDetails$ = new BehaviorSubject<AppliedTimeOff[]>([]);

    pendingApprovalsManagerLoading$ = new BehaviorSubject<boolean>(true);
    pendingApprovalsManagerDetails$ = new BehaviorSubject<AppliedTimeOff[]>([]);

    approvalHistoryLoading$ = new BehaviorSubject<boolean>(true);
    approvalHistoryDetails$ = new BehaviorSubject<AppliedTimeOff[]>([]);

    employeeEmail: string | undefined = '';

    constructor(
        private backendService: BackendService,
        private authService: AuthService,
        private dialog: MatDialog
      ) {}

    getPendingApprovalsList() {
        this.pendingApprovalsLoading$.next(true);
        const email = this.getEmail();

        this.backendService.getPendingTimeOffApprovalsList(email).pipe(
            map((response: AppliedTimeOff[]) => sortPendingApprovalsByToDateAsc(response)),
            tap((sorted) => {
              this.pendingApprovalsLoading$.next(false);
              this.pendingApprovalsDetails$.next(sorted);
            }),
            catchError((error) => {
              this.pendingApprovalsLoading$.next(false);
              this.pendingApprovalsDetails$.next([]);
              throw error;
            })
        ).subscribe({ error: () => {} });
    }

    getPendingApprovalsManagerList() {
        this.pendingApprovalsManagerLoading$.next(true);

        this.backendService.getPendingTimeOffApprovalsManagerList().pipe(
            map((response: AppliedTimeOff[]) => sortPendingApprovalsByToDateAsc(response)),
            tap((sorted) => {
              this.pendingApprovalsManagerLoading$.next(false);
              this.pendingApprovalsManagerDetails$.next(sorted);
            }),
            catchError((error) => {
              this.pendingApprovalsManagerLoading$.next(false);
              this.pendingApprovalsManagerDetails$.next([]);
              throw error;
            })
        ).subscribe({ error: () => {} });
    }

    getApprovalsHistory() {
      this.approvalHistoryLoading$.next(true);
      const email = this.getEmail();

      this.backendService.getApprovalsHistory(email).pipe(
        tap((response: AppliedTimeOff[]) => {
          this.approvalHistoryLoading$.next(false);
          this.approvalHistoryDetails$.next(response);
        }),
        catchError((error) => {
          this.approvalHistoryLoading$.next(false);
          this.approvalHistoryDetails$.next([]);
          throw error;
        })
      ).subscribe({ error: () => {} });
    }

    loadApprovalsData() {
      const email = this.getEmail();
      let hasManagerPermission = false;
      try {
        hasManagerPermission = this.authService.checkPermission('approval_pending_manager_records');
      } catch {
        hasManagerPermission = false;
      }

      this.approvalHistoryLoading$.next(true);
      this.pendingApprovalsLoading$.next(true);
      this.pendingApprovalsManagerLoading$.next(true);

      const requests: any = {
        history: this.backendService.getApprovalsHistory(email),
        pending: this.backendService.getPendingTimeOffApprovalsList(email)
      };
      
      if (hasManagerPermission) {
        requests.manager = this.backendService.getPendingTimeOffApprovalsManagerList();
      }
      
      forkJoin(requests).subscribe({
        next: (results: any) => {
          this.approvalHistoryLoading$.next(false);
          this.approvalHistoryDetails$.next(results.history);

          this.pendingApprovalsLoading$.next(false);
          this.pendingApprovalsDetails$.next(sortPendingApprovalsByToDateAsc(results.pending ?? []));

          // Always clear manager loading — either the request ran and completed, or it was never made
          this.pendingApprovalsManagerLoading$.next(false);
          if (hasManagerPermission && results.manager) {
            this.pendingApprovalsManagerDetails$.next(sortPendingApprovalsByToDateAsc(results.manager));
          }
        },
        error: () => {
          this.approvalHistoryLoading$.next(false);
          this.approvalHistoryDetails$.next([]);
          this.pendingApprovalsLoading$.next(false);
          this.pendingApprovalsDetails$.next([]);
          this.pendingApprovalsManagerLoading$.next(false);
          this.pendingApprovalsManagerDetails$.next([]);
        }
      });
    }

    getEmail() {
        if (this.employeeEmail && this.employeeEmail !== '') {
            return this.employeeEmail;
        }
        
        try {
            return this.authService.getUserSessionData('EMAIL');
        } catch (error) {
            console.error('Failed to get email from session:', error);
            return '';
        }
    }

    openTimeOffDetailsDialogBox(timeOff: AppliedTimeOff, mode: string) {
        this.dialog.open(TimeOffInfoComponent, {
          width: '1000px',
          data: {
            mode: mode,
            timeOff
          }
        });
      }
}